/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import AvalaraValidationModal from "@eisinc/App/Components/AddressSuggestionCard/AvalaraValidationModal";
import Spinner from "@eisinc/App/Components/Spinner/Spinner";
import UploadFilesModal from "@eisinc/App/Components/UploadFilesModal/UploadFilesModal";
import sendContactInfoHandler from "@eisinc/App/Handlers/Data/ContactUs/SendContactInfoHandler";
import { validateRecaptcha } from "@eisinc/App/Services/RecaptchaService";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { doScroll } from "@eisinc/App/Utilities/helpers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { rfqRequestQuoteSpecificationsStyles } from "@eisinc/Overrides/Widgets/RfqRequestQuote/RfqRequestQuoteSpecifications";
import { getCookie, removeCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import submitContactForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SubmitContactForm";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import qs from "qs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import Reaptcha from "reaptcha";
import { css } from "styled-components";

const enum fields {
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    customSettings: getSettingsCollection(state).customSettings,
    Email: state.components.contactUsForm.fieldValues["emailAddress"],
    FirstName: state.components.contactUsForm.fieldValues["firstName"],
    LastName: state.components.contactUsForm.fieldValues["lastName"],
    Phone: state.components.contactUsForm.fieldValues["phoneNumber"],
    Address1: state.components.contactUsForm.fieldValues["addressOne"],
    Address2: state.components.contactUsForm.fieldValues["addressTwo"],
    City: state.components.contactUsForm.fieldValues["city"],
    CompanyName: state.components.contactUsForm.fieldValues["companyName"],
    Country: state.components.contactUsForm.fieldValues["country"],
    Zip: state.components.contactUsForm.fieldValues["postalCode"],
    State: state.components.contactUsForm.fieldValues["state"],
    Comments: state.components.contactUsForm.fieldValues["message"],
    session: state.context.session,
    billTo: getBillToState(state, state?.context?.session?.billToId).value,
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = {
    submitContactForm,
    sendContactInfoHandler,
    clearForm,
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    wrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    messageTextGridItem?: GridItemProps;
    buttonCheckboxWrapper?: InjectableCss;
    buttonGridItem?: GridItemProps;
}

export const contactFormStyles: ContactFormStyles = {
    container: {
        css: css`
            width: 70%;
            margin: 0;
            @media (max-width: 1023px) {
                width: 100% !important;
            }
        `,
    },
    formGridItem: { width: 12 },
    wrapper: {
        css: css`
            &&:not(.shell) > div[class*="Wrapper-sc"] {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
            }
            div[class^="Wrapper"] {
                display: flex;
                flex-wrap: wrap;
                gap: 20px;
                justify-content: space-between;
            }
        `,
    },
    buttonCheckboxWrapper: {
        css: css`
            margin-top: 15px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
        `,
    },
    submitButton: {
        css: css`
            margin-bottom: 10px;
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
};

const styles = contactFormStyles;

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const customStyles = rfqRequestQuoteSpecificationsStyles;

const ContactForm = ({
    id,
    fields,
    clearForm,
    customSettings,
    shellContext,
    sendContactInfoHandler,
    Email,
    FirstName,
    LastName,
    Phone,
    Address1,
    Address2,
    City,
    Country,
    Zip,
    State,
    Comments,
    CompanyName,
    session,
    billTo,
    setFieldValue,
    countries,
}: Props) => {
    const { submitButtonText, successMessage } = fields;

    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadDocuments, setUploadDocuments] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [captchaElm, setCaptchaElm] = useState<any>({} as any);
    const [openDocumentsModal, setOpenDocumentsModal] = useState(false);
    const [returnedFiles, setReturnedFiles] = useState([]);
    const [userVerified, setUserVerified] = useState(false);

    const uploadFilesModalRef: any = useRef(null);
    const avalaraValidation: any = useRef(null);

    const forwardRef = useCallback((node: any) => {
        setCaptchaElm(node as any);
    }, []);

    const getCountry = useMemo(() => {
        return countries?.find(c => c.abbreviation === Country);
    }, [countries, Country]);

    const getState = useMemo(() => {
        return getCountry?.states?.find(s => s.abbreviation === State);
    }, [State]);

    useEffect(() => {
        if (userVerified) {
            continueHandlingFormSubmit();
        }
    }, [userVerified]);

    useEffect(() => {
        const isAuthenticated = (session?.isAuthenticated || session?.rememberMe) && !session?.isGuest;
        if (isAuthenticated) {
            setFieldValue({ key: "emailAddress", value: session.email });
            setFieldValue({ key: "firstName", value: session.firstName });
            setFieldValue({ key: "lastName", value: session.lastName });
            if (billTo) {
                const { address1, address2, city, postalCode, companyName, state, country } = billTo;
                setFieldValue({ key: "addressOne", value: address1 });
                setFieldValue({ key: "addressTwo", value: address2 });
                setFieldValue({ key: "city", value: city });
                setFieldValue({ key: "postalCode", value: postalCode });
                setFieldValue({ key: "companyName", value: companyName });
                setFieldValue({ key: "country", value: country?.abbreviation || "" });
                setFieldValue({ key: "state", value: state?.abbreviation || "" });
            }
        }
    }, [session, billTo]);

    const onRecaptchaVerified = () => {
        setUserVerified(true);
    };

    const signUpToNewLetter = (event: React.SyntheticEvent, value: boolean) => {
        setIsSubscribed(value);
    };

    const buildFormDataForDocuments = (address: any) => {
        const formData = new FormData();
        const SubscribeToList = isSubscribed;

        for (let i = 0; i < returnedFiles.length; i++) {
            const file = returnedFiles[i] as any;

            formData.append(`file[${i}]`, file, file.name || "");
        }

        let utmCollection = getCookie("UtmCollection") ?? "";
        utmCollection =
            utmCollection.indexOf("returnUrl") !== -1 ? decodeURIComponent(utmCollection).split("?")[1] : utmCollection;

        const parsedQueryAttributes = qs.parse(utmCollection);
        const UtmCollectionContainer: { [x: string]: any } = {};

        if (parsedQueryAttributes && Object.keys(parsedQueryAttributes).length !== 0) {
            const utmCollectionFromSettings = (customSettings as any).utmCollection;
            Object.keys(parsedQueryAttributes).forEach(key => {
                if (utmCollectionFromSettings.indexOf(key) !== -1) {
                    UtmCollectionContainer[key] = parsedQueryAttributes[key];
                }
            });

            Object.keys(UtmCollectionContainer).length !== 0 &&
                formData.append(`UtmCollection`, JSON.stringify(UtmCollectionContainer) as any);
        } else {
            removeCookie("UtmCollection");
        }

        formData.append(`Email`, address.email);
        formData.append(`FirstName`, address.firstName);
        formData.append(`LastName`, address.lastName);
        formData.append(`Phone`, address.phone);
        formData.append(`CompanyName`, address.companyName);
        formData.append(`Country`, address.country?.abbreviation ? address.country?.abbreviation : address.country);
        formData.append(`Address1`, address.address1);
        Address2 && formData.append(`Address2`, address.address2);
        formData.append(`Zip`, address.postalCode);
        formData.append(`City`, address.city);
        formData.append(`State`, address.state?.abbreviation ? address.state?.abbreviation : address.state);
        Comments && formData.append(`Comments`, Comments);
        SubscribeToList && formData.append(`SubscribeToList`, SubscribeToList.toString());

        return formData;
    };

    const validateAddressByAvalara = async () => {
        const address = {
            email: Email,
            firstName: FirstName,
            lastName: LastName,
            phone: Phone,
            companyName: CompanyName,
            country: Country,
            address1: Address1,
            address2: Address2,
            city: City,
            state: State,
            postalCode: Zip,
        };

        let updatedAddress: any;

        try {
            updatedAddress = await avalaraValidation.current.validate(address, () => {
                setIsSubmitting(false);
            });
        } catch (e) {
            updatedAddress = address;
        }

        return updatedAddress;
    };

    const doValidation = () => {
        const widgetValidators: Validator[] = [];

        Object.keys(validators).forEach(key => {
            const validator = validators[key];

            if (validator) {
                widgetValidators.push(validator);
            }
        });

        const results = widgetValidators.map(v => v());

        return results.every(o => o);
    };

    const continueHandlingFormSubmit = () => {
        if (!doValidation()) {
            doScroll();

            return;
        }

        setErrorMessage("");
        setIsSubmitting(true);

        captchaElm.getResponse().then((response: any) => {
            validateRecaptcha(response).then(
                async () => {
                    const newAddress = await validateAddressByAvalara();

                    setIsSubmitting(true);

                    const formData = buildFormDataForDocuments(newAddress);

                    sendContactInfoHandler({
                        formData,
                        onSuccess: () => {
                            setIsModalOpen(true);
                            setIsSubmitting(false);

                            if ((customSettings as any).deleteUtmCookieOnFormSubmit.toLowerCase() === "true") {
                                removeCookie("UtmCollection");
                            }
                        },
                        onComplete() {
                            this.onSuccess?.("");
                        },
                        onError: (error: any) => {
                            setErrorMessage(error);
                            setIsModalOpen(true);
                            setIsSubmitting(false);
                        },
                    });
                },
                () => {
                    captchaElm.execute();
                },
            );
        });
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userVerified) {
            captchaElm.execute();
            return;
        }

        continueHandlingFormSubmit();
    };

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        setIsSubscribed(false);
        setUploadDocuments(false);
        uploadFilesModalRef?.current?.resetFields();
        clearForm();
    };

    const modalCloseHandler = () => setIsModalOpen(false);

    const typedAddress: ShipToModel = useMemo(
        () =>
            ({
                address1: Address1,
                address2: Address2,
                city: City,
                state: getState,
                postalCode: Zip,
                country: getCountry,
            }) as ShipToModel,
        [Address1, Address2, City, getState, Zip, getCountry],
    );

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <ContactFormContext.Provider value={{ validators }}>
                    <form id="contactForm" onSubmit={handleFormSubmit} noValidate style={{ position: "relative" }}>
                        <StyledWrapper {...styles.wrapper} className={shellContext.isInShell ? "shell" : ""}>
                            <Zone contentId={id} zoneName="ContentOne" />
                            <Typography
                                as="h4"
                                size="24px"
                                lineHeight="26px"
                                css={css`
                                    margin: 20px 0;
                                `}
                            >
                                {translate("Company Info")}
                            </Typography>
                            <Zone contentId={id} zoneName="ContentTwo" {...styles.wrapper} />
                        </StyledWrapper>
                        <Checkbox
                            {...customStyles.uploadDocumentsCheckbox}
                            onChange={(e, value) => {
                                setUploadDocuments(value);
                                setOpenDocumentsModal(value);

                                if (!value) {
                                    uploadFilesModalRef.current.resetFields();
                                }
                            }}
                            checked={uploadDocuments}
                        >
                            {translate("Click Here if would like to attach additional documents")}
                        </Checkbox>
                        <UploadFilesModal
                            ref={uploadFilesModalRef}
                            openModal={openDocumentsModal}
                            headline={translate("Upload Documents")}
                            handleClose={() => {
                                setOpenDocumentsModal(false);
                                setUploadDocuments(false);
                            }}
                            handleContinue={files => {
                                setOpenDocumentsModal(false);
                                setReturnedFiles(files);

                                if (!files.length) {
                                    setUploadDocuments(false);
                                }
                            }}
                        />
                        <StyledWrapper {...styles.buttonCheckboxWrapper}>
                            <Checkbox
                                {...customStyles.uploadDocumentsCheckbox}
                                onChange={signUpToNewLetter}
                                checked={isSubscribed}
                            >
                                {translate("Sign Me Up for newsletters")}
                            </Checkbox>
                            <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                {submitButtonText}
                            </Button>
                        </StyledWrapper>
                        <Reaptcha
                            ref={forwardRef}
                            sitekey={(customSettings as any).reCaptchaSitekey}
                            size="invisible"
                            onVerify={onRecaptchaVerified}
                            onExpire={() => {
                                setUserVerified(false);
                            }}
                        />
                        <AvalaraValidationModal ref={avalaraValidation} address={typedAddress} />
                        <Modal
                            headline={translate("Message Sent")}
                            contentLabel={successMessage}
                            {...styles.successModal}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer>
                                <GridItem {...styles.messageTextGridItem}>
                                    <Typography>{errorMessage || successMessage}</Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button onClick={continueButtonClickHandler}>{translate("Continue")}</Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                        {isSubmitting && <Spinner />}
                    </form>
                </ContactFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(ContactForm)),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
